/* eslint-disable no-underscore-dangle */
/** @jsx jsx */
import { useState, useEffect, useRef } from "react"
import { Flex, jsx, Container, Box, Button } from "theme-ui"
import { GatsbyImage } from "gatsby-plugin-image"
import { Global } from "@emotion/react"
import styled from "@emotion/styled"
import queryString from "query-string"
import { useNavLinks } from "../hooks/useNavLinks"
import Icon from "./shared/Icon"
import Action from "./shared/Action"
import Link from "./shared/Link"

// Hook for detecting click/touch outside of nav
export const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler(event)
    }
    document.addEventListener("mousedown", listener)
    return () => {
      document.removeEventListener("mousedown", listener)
    }
  }, [ref, handler])
}

// Burger button
const StyledBurger = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 2rem;
  height: 1.75rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  &:focus {
    outline: none;
  }
  div {
    width: 1.75rem;
    height: 0.25rem;
    background: ${(t) => t.theme.colors.text};
    border-radius: 10px;
    transition: all 0.3s linear;
    position: relative;
    transform-origin: 1px;
    :first-of-type {
      transform: ${({ open }) => (open ? "rotate(45deg)" : "rotate(0)")};
    }
    :nth-of-type(2) {
      opacity: ${({ open }) => (open ? "0" : "1")};
      transform: ${({ open }) => (open ? "translateX(20px)" : "translateX(0)")};
    }
    :nth-of-type(3) {
      transform: ${({ open }) => (open ? "rotate(-45deg)" : "rotate(0)")};
    }
  }
`

const Burger = ({ open, openToggler }) => (
  <StyledBurger open={open} onClick={() => openToggler(!open)}>
    <div />
    <div />
    <div />
  </StyledBurger>
)

// Menu/wrapper for nav elements/links
const StyledMenu = styled.div`
  display: flex;
  justify-content: flex-end;
  background-color: ${(t) => t.theme.colors.white};
  z-index: 1;
  opacity: 1;
  height: 100vh;
  width: ${({ open }) => (open ? "100vw" : "0px")};
  position: absolute;
  top: 96px;
  right: 0;
  transform: ${({ open }) => (open ? "translateX(0)" : "translateX(100%)")};
  transition: all 0.3s ease-in-out;
  overflow: hidden;
`

// Collapsible Nav Component
const CollapsibleDiv = styled.div`
  transition: opacity 0.4s ease;
`

const CollapsibleNavGroup = ({ links, onFinish }) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggle = () => setIsOpen(!isOpen)
  const { dropdownTitle } = links

  return (
    <Box
      sx={{
        textAlign: "right",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        padding: "5px 0",
      }}
    >
      <Button
        onClick={toggle}
        variant="link"
        sx={{
          display: "flex",
          alignItems: "center",
          fontWeight: 800,
          "&:hover": {
            cursor: "pointer",
          },
        }}
      >
        {dropdownTitle}
        <Icon icon="down-chevron" sx={{ transform: isOpen && "rotate(180deg)", minWidth: "14px", minHeight: "14px" }} />
      </Button>
      <CollapsibleDiv
        isOpen={isOpen}
        aria-expanded={isOpen}
        sx={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? "visible" : "hidden",
        }}
      >
        <Flex
          as="ul"
          sx={{
            flexDirection: "column",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          {links.links.map((link) =>
            link.__typename === "ContentfulNavDropdown" ? (
              <CollapsibleSecondaryNavGroup key={link.id} dropdown={link} onFinish={onFinish} />
            ) : (
              <li key={link.id} sx={{ variant: "collapsibleNavListItem" }}>
                <Action
                  key={link.id}
                  {...link}
                  onFinish={() => {
                    onFinish(false)
                  }}
                  sx={{
                    fontSize: "14px",
                    marginRight: "20px",
                    color: "text",
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                />
              </li>
            ),
          )}
        </Flex>
      </CollapsibleDiv>
    </Box>
  )
}

const CollapsibleSecondaryNavGroup = ({ dropdown, onFinish }) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggle = () => setIsOpen(!isOpen)
  const { dropdownTitle, dropdownMainLink } = dropdown

  return (
    <Box>
      <Flex
        sx={{
          alignItems: "center",
          textAlign: "right",
          padding: "8px 16px",
          justifyContent: "flex-end",
        }}
      >
        <Action
          key={dropdown.id}
          url={dropdownMainLink}
          label={dropdownTitle}
          width="inherit"
          paddingCustom={[0]}
          sx={{
            fontSize: "14px",
            marginRight: "20px",
            color: "text",
            textAlign: "right",
            "&:hover": {
              cursor: "pointer",
            },
          }}
        />
        <Button
          onClick={toggle}
          variant="link"
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 800,
            padding: 0,
            flexShrink: 0,
            "&:hover": {
              cursor: "pointer",
            },
          }}
        >
          <Icon
            icon="down-chevron"
            sx={{ transform: isOpen && "rotate(180deg)", minWidth: "14px", minHeight: "14px" }}
          />
        </Button>
      </Flex>
      <CollapsibleDiv
        isOpen={isOpen}
        aria-expanded={isOpen}
        sx={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? "visible" : "hidden",
        }}
      >
        <Flex
          as="ul"
          sx={{
            flexDirection: "column",
            justifyContent: "flex-end",
            width: "100%",
            pl: 0,
            pr: "14px",
          }}
        >
          {dropdown.links.map((link) => (
            <li key={link.id} sx={{ variant: "collapsibleNavListItem" }}>
              <Action
                key={link.id}
                {...link}
                onFinish={() => {
                  onFinish(false)
                }}
                fontColor="text"
                sx={{
                  fontSize: "14px",
                  marginRight: "20px",
                  color: "text",
                  "&:hover": {
                    cursor: "pointer",
                  },
                }}
              />
            </li>
          ))}
        </Flex>
      </CollapsibleDiv>
    </Box>
  )
}

const Menu = ({ open, navElements, openToggler }) => {
  const { links } = navElements
  return (
    <StyledMenu open={open}>
      <Container
        as="ul"
        sx={{
          fontSize: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
        }}
        className="mobile-nav"
      >
        {links.map((link) => {
          const commonProps = {
            key: link.id,
            onFinish: openToggler,
          }

          if (link.__typename === "ContentfulNavDropdown") {
            return link.dropdownMainLink ? (
              <CollapsibleSecondaryNavGroup dropdown={link} {...commonProps} />
            ) : (
              <CollapsibleNavGroup links={link} {...commonProps} />
            )
          }

          return (
            <li key={link.id} sx={{ variant: "navListItem" }}>
              <Action
                url={link.url}
                actionEvent={link.actionEvent}
                label={link.label}
                classname={link.classname}
                icon={!link.label && link.icon}
                iconSize={link.iconSize}
                style={link.style}
                form={link.form}
                hubspotForm={link.hubspotForm}
                onFinish={() => openToggler(false)}
                mFontsize={20}
                paddingCustom={!link.style && "5px 36px 5px 20px"}
                fontWeight={800}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "right",
                  textDecoration: "none",
                  "&:hover": {
                    cursor: "pointer",
                  },
                }}
              />
            </li>
          )
        })}
      </Container>
    </StyledMenu>
  )
}

// Mobile Nav component
const MobileNav = () => {
  const [open, setOpen] = useState(false)
  const node = useRef()
  useOnClickOutside(node, () => setOpen(false))
  const { contentfulMainNav } = useNavLinks()
  let queryParams = ""
  if (typeof window !== "undefined") {
    queryParams = queryString.parse(window.location.search)
  }
  const { banner } = queryParams
  return (
    <Container
      ref={node}
      sx={{
        display: ["flex", "flex", "flex", "flex"],
        justifyContent: "space-between",
        height: "96px",
        alignItems: "center",
      }}
    >
      {open && (
        <Global
          styles={() => ({
            body: {
              overflow: "hidden",
            },
          })}
        />
      )}
      <Box sx={{ maxWidth: "210px" }}>
        <Link to={banner ? "/" + "?banner=" + banner : "/"}>
          <GatsbyImage image={contentfulMainNav.logo.gatsbyImageData} alt={contentfulMainNav.logo.description} />
        </Link>
      </Box>
      <Burger open={open} openToggler={setOpen} />
      <Menu open={open} navElements={contentfulMainNav} openToggler={setOpen} />
    </Container>
  )
}

export default MobileNav
